import type { UserMenuEntry } from "@/config/userMenu";
import type { LicenseModule } from "./moduleConfig";
import type { ModifierType } from "./userModifierEnum";

type IsModuleActive = (module: LicenseModule) => boolean;
type IsModifierSet = (modifier: ModifierType) => boolean;

function hasAccess(
  hasRight: (right: string) => boolean,
  requiredRight: UserMenuEntry["accessRight"],
  some = false,
) {
  if (requiredRight == null) return true;
  if (typeof requiredRight === "string") return hasRight(requiredRight);
  return some ? requiredRight.some(hasRight) : requiredRight.every(hasRight);
}

function filterMenuEntry(
  entry: UserMenuEntry,
  hasRight: (right: string) => boolean,
  anonym: boolean,
  isModifierSet: IsModifierSet,
  isModuleActive: IsModuleActive,
): UserMenuEntry | undefined {
  if (entry.onlyOnModifier != null && !isModifierSet(entry.onlyOnModifier)) return undefined;
  if (entry.onlyOnModule != null && !isModuleActive(entry.onlyOnModule)) return undefined;

  const canOpenEntry = hasAccess(hasRight, entry.accessRight, entry.accessRightSome);
  const filteredChildren = entry.child
    ?.map((child) => filterMenuEntry(child, hasRight, anonym, isModifierSet, isModuleActive))
    .filter((child): child is UserMenuEntry => child != null);
  const href = canOpenEntry && !(entry.hideOnAnonym && anonym) ? entry.href : undefined;

  if (href == null && (filteredChildren == null || filteredChildren.length === 0)) return undefined;

  const visibleEntry = { ...entry, href, child: filteredChildren };
  delete visibleEntry.accessRight;
  delete visibleEntry.accessRightSome;
  delete visibleEntry.hideOnAnonym;
  return visibleEntry;
}

export function filterUserMenu(
  entries: readonly UserMenuEntry[],
  hasRight: (right: string) => boolean,
  anonym: boolean,
  isModifierSet: IsModifierSet = () => true,
  isModuleActive: IsModuleActive = () => true,
) {
  return entries
    .map((entry) => filterMenuEntry(entry, hasRight, anonym, isModifierSet, isModuleActive))
    .filter((entry): entry is UserMenuEntry => entry != null);
}
